import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import aiCostService, { AI_PRICING } from '../services/aiCostService';

/**
 * AI Cost Management Page
 * AI 서비스 사용량 및 비용을 모니터링하고 예산을 관리합니다.
 */
const AiCost = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [budgetSettings, setBudgetSettings] = useState(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  // 차트 색상
  const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

  // 통계 로드
  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await aiCostService.getAiUsageStats(days);
      if (result.success) {
        setStats(result.stats);
      } else {
        setError('통계를 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Failed to load AI stats:', err);
      // API가 없는 경우 샘플 데이터 사용
      setStats({
        days,
        summary: {
          totalCalls: 0,
          totalTokens: 0,
          totalCost: 0,
          successRate: 100,
          uniqueUsers: 0
        },
        today: {
          calls: 0,
          tokens: 0,
          cost: 0
        },
        byContentType: [],
        byFeature: [],
        byProvider: [],
        chartData: []
      });
    }
    setLoading(false);
  }, [days]);

  // 예산 설정 로드
  const loadBudgetSettings = useCallback(() => {
    const settings = aiCostService.getBudgetSettings();
    setBudgetSettings(settings);
  }, []);

  useEffect(() => {
    loadStats();
    loadBudgetSettings();
  }, [loadStats, loadBudgetSettings]);

  // 예산 저장
  const handleSaveBudget = (newSettings) => {
    aiCostService.saveBudgetSettings(newSettings);
    setBudgetSettings(newSettings);
    setShowBudgetModal(false);
  };

  // 비용 포맷팅
  const formatCost = (cost) => {
    if (cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  // 토큰 포맷팅
  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(2)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  // 예산 사용률 계산
  const getBudgetUsage = () => {
    if (!stats || !budgetSettings) return 0;
    const monthCost = stats.summary?.totalCost || 0;
    const budget = budgetSettings.monthlyBudget || 50;
    return Math.min((monthCost / budget) * 100, 100);
  };

  // 예산 상태 가져오기
  const getBudgetStatus = () => {
    const usage = getBudgetUsage();
    if (usage >= 100) return 'danger';
    if (usage >= budgetSettings?.alertThreshold) return 'warning';
    return 'normal';
  };

  // 제공업체 정보 생성
  const getProvidersList = () => {
    return Object.entries(AI_PRICING).map(([key, provider]) => {
      const mainModel = Object.keys(provider.models)[0];
      const pricing = provider.models[mainModel];
      const usageData = stats?.byProvider?.find(p =>
        p.provider?.toLowerCase() === key ||
        p.provider?.toLowerCase().includes(key)
      );

      return {
        id: key,
        name: provider.name,
        model: mainModel,
        inputPrice: pricing.input,
        outputPrice: pricing.output,
        tier: provider.tier,
        status: provider.status,
        usage: usageData || { count: 0, tokens: 0, cost: 0 }
      };
    });
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">AI 사용량 통계를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  const budgetUsage = getBudgetUsage();
  const budgetStatus = getBudgetStatus();
  const providers = getProvidersList();

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex justify-between items-start mb-6 gap-4 max-md:flex-col max-md:items-stretch">
        <div>
          <h2 className="text-xl font-bold text-gray-900 m-0 mb-2">AI 비용 관리</h2>
          <p className="text-sm text-gray-600 m-0">AI 서비스 사용량 및 비용을 모니터링합니다.</p>
        </div>
        <div className="flex gap-2 flex-shrink-0 max-md:flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="py-2 px-3 border border-gray-200 rounded-md text-[13px] text-gray-900 bg-white cursor-pointer"
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={30}>최근 30일</option>
            <option value={90}>최근 90일</option>
          </select>
          <button
            className="py-2 px-4 bg-white text-blue-600 border border-blue-600 rounded-md text-[13px] font-medium cursor-pointer transition-all hover:bg-blue-50"
            onClick={() => setShowBudgetModal(true)}
          >
            예산 설정
          </button>
          <button
            className="py-2 px-4 bg-blue-600 text-white border-none rounded-md text-[13px] font-medium cursor-pointer transition-colors hover:bg-blue-700"
            onClick={loadStats}
          >
            새로고침
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-600 rounded-lg py-3 px-4 mb-6 flex items-center gap-2">
          <span className="text-lg">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* 비용 요약 카드 */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-8 max-md:grid-cols-2 max-sm:grid-cols-1">
        <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 relative transition-all hover:shadow-md">
          <div className="text-3xl">💰</div>
          <div className="flex flex-col gap-1 flex-1">
            <span className="text-2xl font-bold text-gray-900">{formatCost(stats?.today?.cost || 0)}</span>
            <span className="text-[13px] text-gray-600">오늘 비용</span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 relative transition-all hover:shadow-md">
          <div className="text-3xl">📊</div>
          <div className="flex flex-col gap-1 flex-1">
            <span className="text-2xl font-bold text-gray-900">{formatCost(stats?.summary?.totalCost || 0)}</span>
            <span className="text-[13px] text-gray-600">이번 달 비용</span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 relative transition-all hover:shadow-md">
          <div className="text-3xl">🎯</div>
          <div className="flex flex-col gap-1 flex-1">
            <span className="text-2xl font-bold text-gray-900">{formatCost(budgetSettings?.monthlyBudget || 50)}</span>
            <span className="text-[13px] text-gray-600">월 예산</span>
          </div>
        </div>
        <div className={`bg-white border rounded-xl p-5 flex items-center gap-4 relative transition-all hover:shadow-md ${
          budgetStatus === 'warning' ? 'border-amber-600 bg-amber-50' :
          budgetStatus === 'danger' ? 'border-red-600 bg-red-50' : 'border-gray-200'
        }`}>
          <div className="text-3xl">📈</div>
          <div className="flex flex-col gap-1 flex-1">
            <span className={`text-2xl font-bold ${
              budgetStatus === 'warning' ? 'text-amber-600' :
              budgetStatus === 'danger' ? 'text-red-600' : 'text-gray-900'
            }`}>{budgetUsage.toFixed(1)}%</span>
            <span className="text-[13px] text-gray-600">예산 대비 사용률</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 rounded-b-xl overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                budgetStatus === 'warning' ? 'bg-amber-600' :
                budgetStatus === 'danger' ? 'bg-red-600' : 'bg-emerald-600'
              }`}
              style={{ width: `${budgetUsage}%` }}
            />
          </div>
        </div>
      </div>

      {/* 토큰 사용량 요약 */}
      <div className="mb-8">
        <h3 className="text-base font-semibold text-gray-900 m-0 mb-4">토큰 사용량</h3>
        <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
          <div className="bg-white border border-gray-200 rounded-xl py-4 px-5 flex items-center gap-3">
            <span className="text-2xl">🔢</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xl font-bold text-gray-900">{formatTokens(stats?.summary?.totalTokens || 0)}</span>
              <span className="text-xs text-gray-600">총 토큰</span>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl py-4 px-5 flex items-center gap-3">
            <span className="text-2xl">📞</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xl font-bold text-gray-900">{stats?.summary?.totalCalls || 0}</span>
              <span className="text-xs text-gray-600">API 호출 수</span>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl py-4 px-5 flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xl font-bold text-gray-900">{stats?.summary?.successRate || 100}%</span>
              <span className="text-xs text-gray-600">성공률</span>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl py-4 px-5 flex items-center gap-3">
            <span className="text-2xl">👤</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xl font-bold text-gray-900">{stats?.summary?.uniqueUsers || 0}</span>
              <span className="text-xs text-gray-600">사용자 수</span>
            </div>
          </div>
        </div>
      </div>

      {/* 비용 추이 차트 */}
      {stats?.chartData?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 m-0 mb-5">비용 추이</h3>
          <div className="w-full min-h-[250px]">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#525252', fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fill: '#525252', fontSize: 12 }}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'cost') return [`$${value.toFixed(4)}`, '비용'];
                    if (name === 'tokens') return [formatTokens(value), '토큰'];
                    return [value, '호출 수'];
                  }}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('ko-KR')}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="cost"
                  name="비용"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 콘텐츠 타입별 사용량 */}
      {stats?.byContentType?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 m-0 mb-5">콘텐츠 타입별 사용량</h3>
          <div className="grid grid-cols-2 gap-6 max-lg:grid-cols-1">
            <div className="min-h-[250px]">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.byContentType}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.byContentType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => [
                      `${value}회 (${formatCost(props.payload.cost)})`,
                      props.payload.name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 text-left border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">타입</th>
                    <th className="p-3 text-left border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">호출 수</th>
                    <th className="p-3 text-left border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">토큰</th>
                    <th className="p-3 text-left border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">비용</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byContentType.map((item, index) => (
                    <tr key={item.contentType}>
                      <td className="p-3 text-left border-b border-gray-200 text-sm text-gray-900">
                        <span className="mr-2">{item.icon}</span>
                        {item.name}
                      </td>
                      <td className="p-3 text-left border-b border-gray-200 text-sm text-gray-900">{item.count}</td>
                      <td className="p-3 text-left border-b border-gray-200 text-sm text-gray-900">{formatTokens(item.tokens)}</td>
                      <td className="p-3 text-left border-b border-gray-200 text-sm text-gray-900">{formatCost(item.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 제공업체별 사용량 */}
      {stats?.byProvider?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 m-0 mb-5">제공업체별 사용량</h3>
          <div className="w-full min-h-[250px]">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.byProvider}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="provider" tick={{ fill: '#525252', fontSize: 12 }} />
                <YAxis tick={{ fill: '#525252', fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'cost') return [formatCost(value), '비용'];
                    if (name === 'tokens') return [formatTokens(value), '토큰'];
                    return [value, '호출 수'];
                  }}
                />
                <Legend />
                <Bar dataKey="count" name="호출 수" fill="#3B82F6" />
                <Bar dataKey="cost" name="비용" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 제공업체별 단가 정보 */}
      <div className="mb-6">
        <h3 className="text-base font-semibold text-gray-900 m-0 mb-4">제공업체별 단가 ($/1M tokens)</h3>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4 max-md:grid-cols-1">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className={`bg-white border border-gray-200 rounded-xl p-5 transition-all hover:shadow-md ${provider.status === 'inactive' ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-base font-semibold text-gray-900">{provider.name}</span>
                <span className={`text-[11px] font-semibold py-1 px-2 rounded-xl ${
                  provider.tier === 'free' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {provider.tier === 'free' ? '무료' : '유료'}
                </span>
              </div>
              <div className="text-[13px] text-gray-600 mb-4 font-mono">{provider.model}</div>
              <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-gray-600">Input</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {provider.inputPrice === 0 ? '무료' : `$${provider.inputPrice}`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-gray-600">Output</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {provider.outputPrice === 0 ? '무료' : `$${provider.outputPrice}`}
                  </span>
                </div>
              </div>
              {provider.usage.count > 0 && (
                <div className="flex justify-between mt-3 pt-3 border-t border-gray-200">
                  <span className="text-[13px] font-semibold text-blue-600">{provider.usage.count}회</span>
                  <span className="text-[13px] font-semibold text-blue-600">{formatCost(provider.usage.cost)}</span>
                </div>
              )}
              <div className={`mt-3 text-xs font-medium text-center py-1.5 px-3 rounded-md ${
                provider.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-gray-500'
              }`}>
                {provider.status === 'active' ? '사용중' : '미사용'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 예산 안내 */}
      <div className="bg-blue-50 border border-blue-600 rounded-xl py-4 px-5">
        <p className="m-0 mb-2 text-sm text-gray-900">💡 예산 임계값({budgetSettings?.alertThreshold || 80}%)에 도달하면 알림이 표시됩니다.</p>
        <p className="m-0 text-sm text-gray-900">비용 계산은 실제 API 호출 시 기록된 토큰 수를 기반으로 합니다.</p>
      </div>

      {/* 예산 설정 모달 */}
      {showBudgetModal && (
        <BudgetSettingsModal
          settings={budgetSettings}
          onSave={handleSaveBudget}
          onClose={() => setShowBudgetModal(false)}
        />
      )}
    </div>
  );
};

/**
 * 예산 설정 모달 컴포넌트
 */
const BudgetSettingsModal = ({ settings, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    monthlyBudget: settings?.monthlyBudget || 50,
    dailyLimit: settings?.dailyLimit || 5,
    alertThreshold: settings?.alertThreshold || 80,
    notifications: settings?.notifications ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-[400px] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 m-0">예산 설정</h3>
          <button className="bg-transparent border-none text-2xl text-gray-600 cursor-pointer p-0 leading-none hover:text-gray-900" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-900 mb-2">월 예산 ($)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.monthlyBudget}
              onChange={(e) => setFormData({ ...formData, monthlyBudget: parseFloat(e.target.value) || 0 })}
              className="w-full py-2.5 px-3 border border-gray-200 rounded-md text-sm text-gray-900 box-border focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10"
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-900 mb-2">일일 한도 ($)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={formData.dailyLimit}
              onChange={(e) => setFormData({ ...formData, dailyLimit: parseFloat(e.target.value) || 0 })}
              className="w-full py-2.5 px-3 border border-gray-200 rounded-md text-sm text-gray-900 box-border focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10"
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-900 mb-2">알림 임계값 (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.alertThreshold}
              onChange={(e) => setFormData({ ...formData, alertThreshold: parseInt(e.target.value) || 80 })}
              className="w-full py-2.5 px-3 border border-gray-200 rounded-md text-sm text-gray-900 box-border focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10"
            />
            <span className="block text-xs text-gray-500 mt-1">예산의 {formData.alertThreshold}% 도달 시 알림</span>
          </div>
          <div className="mb-5">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-900">
              <input
                type="checkbox"
                checked={formData.notifications}
                onChange={(e) => setFormData({ ...formData, notifications: e.target.checked })}
                className="w-[18px] h-[18px] cursor-pointer"
              />
              알림 활성화
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="py-2.5 px-5 bg-white text-gray-600 border border-gray-200 rounded-md text-sm font-medium cursor-pointer transition-all hover:bg-slate-100"
              onClick={onClose}
            >
              취소
            </button>
            <button
              type="submit"
              className="py-2.5 px-5 bg-blue-600 text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700"
            >
              저장
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AiCost;
